/**
 * 分子话题声量走势 Hook
 */
import { useState, useCallback, useEffect } from 'react';
import {
  getWeeklyReportTopicTimeline,
  ISubTopicTrendResponse,
} from '@op/services/weeklyReport';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { DBO_REQ } from '@op/utils/format';
import type {
  TRequestBackType,
  TRequestPieBackType,
} from '@op/utils/requestUtil';

interface IUseSubTopicTrendOptions {
  autoFetch?: boolean;
}

type FetchDataFunc = (filter?: any) => Promise<void>;

export type TSubTopicNameMapItem = {
  cn: string;
  en: string;
  category: string;
  sub_category: string;
};

export interface IUseSubTopicTrendResult {
  data: ISubTopicTrendResponse | null;
  loading: boolean;
  error: boolean;
  lineChartData: TRequestBackType | undefined;
  pieChartData: TRequestPieBackType | undefined;
  summary: ISubTopicTrendResponse['summary'] | null;
  linkTime: { start_time: string; end_time: string } | null;
  // 中英文映射对象
  nameMap: Record<string, TSubTopicNameMapItem>;
  fetchData: FetchDataFunc;
}

// ========== 数据处理函数 ==========

/**
 * 获取子话题的显示名称
 * 如果是 'Other'/'Others'，则显示为 "{category} - {sub_category}"
 * @param item - 包含 category 和 sub_category 的对象
 * @param lang - 语言，'cn' 或 'en'，默认 'en'（也作为 key 使用）
 */
const getSubCategoryName = (
  item: {
    category_cn: string;
    category_en: string;
    sub_category_cn: string;
    sub_category_en: string;
  },
  lang: 'cn' | 'en' = 'en',
) => {
  const isOther =
    item.sub_category_en.toLowerCase() === 'other' ||
    item.sub_category_en.toLowerCase() === 'others';

  if (isOther) {
    return lang === 'cn'
      ? `${item.category_cn} - ${item.sub_category_cn}`
      : `${item.category_en} - ${item.sub_category_en}`;
  }

  return lang === 'cn' ? item.sub_category_cn : item.sub_category_en;
};

/**
 * 构建中英文名称映射
 */
const buildNameMap = (data: ISubTopicTrendResponse) => {
  const map: Record<string, TSubTopicNameMapItem> = {};
  // 从 list 中提取所有子话题的中英文名称
  data.list.forEach(item => {
    const key = getSubCategoryName(item, 'en');
    map[key] = {
      cn: getSubCategoryName(item, 'cn'),
      en: getSubCategoryName(item, 'en'),
      category: item.category_en,
      sub_category: item.sub_category_en,
    };
  });

  return map;
};

/**
 * 将 trend 预处理为长格式数据，供 DBO_REQ.moreKeys 使用
 * 子话题 trend 是扁平数组，需要按日期分组
 */
const preprocessTrendDataToLongFormat = (data: ISubTopicTrendResponse) => {
  const { trend } = data;

  // 按日期分组
  const groupedByDate = trend.reduce<Record<string, any>>((acc, item) => {
    if (!acc[item.date]) {
      acc[item.date] = { date: item.date };
    }
    // 使用 getSubCategoryName 作为系列名（Other 时会带上 category）
    const key = getSubCategoryName(item, 'en');
    acc[item.date][key] = item.mentions;
    return acc;
  }, {});

  return Object.values(groupedByDate);
};

/**
 * 使用 DBO_REQ.moreKeys 将 trend 转换为折线图格式
 */
const transformTrendDataToLineChart = (
  data: ISubTopicTrendResponse,
  filter: any,
): TRequestBackType => {
  const longFormatData = preprocessTrendDataToLongFormat(data);
  return DBO_REQ.moreKeys({
    timeline: longFormatData,
    timeKey: 'date',
    filter,
    timeline_of_start_time: data.trend_time.start_time,
    timeline_of_end_time: data.trend_time.end_time,
    needTable: true,
    moreLineTable: true,
  });
};

/**
 * 将 list 转换为饼图格式
 * 使用 getSubCategoryName 作为系列名
 *
 * @returns TRequestPieBackType
 *   - series: 饼图系列数据（name + value）
 *   - legend: 图例数据（所有子话题英文名）
 *   - isEmpty: 是否为空
 *   - totalSeries: 总声量
 */
const transformListDataToPieChart = (
  data: ISubTopicTrendResponse,
): TRequestPieBackType => {
  const { list } = data;

  // 先对原始数据排序：other 相关项放最后，其他按 mentions 降序
  const sortedList = [...list].sort((a, b) => {
    const isAOthers = a.sub_category_en.toLowerCase() === 'others';
    const isBOthers = b.sub_category_en.toLowerCase() === 'other';

    if (isAOthers && !isBOthers) return 1;
    if (!isAOthers && isBOthers) return -1;
    return (b?.mentions || 0) - (a?.mentions || 0);
  });

  const series: Array<{ name: string; value: number }> = [];
  const legend: string[] = [];
  let totalSeries = 0;

  for (const item of sortedList) {
    const value = item?.mentions || 0;
    const key = getSubCategoryName(item, 'en');
    series.push({ name: key, value });
    legend.push(key);
    totalSeries += value;
  }

  return {
    series,
    legend,
    isEmpty: totalSeries === 0,
    totalSeries,
  };
};

// ========== Hook ==========

export const useSubTopicTrend = (
  filter: any,
  options?: IUseSubTopicTrendOptions,
): IUseSubTopicTrendResult => {
  const { autoFetch = true } = options || {};
  const [data, setData] = useState<ISubTopicTrendResponse | null>(null);
  const [lineChartData, setLineChartData] = useState<TRequestBackType>();
  const [pieChartData, setPieChartData] = useState<TRequestPieBackType>();
  const [summary, setSummary] = useState<
    ISubTopicTrendResponse['summary'] | null
  >(null);
  const [linkTime, setLinkTime] = useState<{
    start_time: string;
    end_time: string;
  } | null>(null);
  const [nameMap, setNameMap] = useState<
    Record<string, { cn: string; en: string }>
  >({});

  const { loading, error, wrapperCancelRequest } =
    useCancelRequestHook<ISubTopicTrendResponse>({
      clear: () => {
        setData(null);
        setLineChartData(undefined);
        setPieChartData(undefined);
        setSummary(null);
        setLinkTime(null);
        setNameMap({});
      },
    });

  const fetchData = useCallback(async (requestFilter?: any) => {
    if (!requestFilter?.start_time || !requestFilter?.end_time) {
      return;
    }

    try {
      const res = await wrapperCancelRequest(
        getWeeklyReportTopicTimeline,
        requestFilter,
      );
      const resultData = res || null;
      setData(resultData);
      // 直接处理图表数据
      if (resultData) {
        setLineChartData(
          transformTrendDataToLineChart(resultData, requestFilter),
        );
        setPieChartData(transformListDataToPieChart(resultData));
        setSummary(resultData.summary || null);
        setLinkTime(resultData.link_time || null);
        setNameMap(buildNameMap(resultData));
      } else {
        setLineChartData(undefined);
        setPieChartData(undefined);
        setSummary(null);
        setLinkTime(null);
        setNameMap({});
      }
    } catch (err) {
      console.log('获取分子话题声量走势失败:', err);
    }
  }, []);

  useEffect(() => {
    if (!filter || !autoFetch) return;
    fetchData(filter);
  }, [filter, autoFetch, fetchData]);

  return {
    data,
    loading,
    error,
    lineChartData,
    pieChartData,
    summary,
    linkTime,
    nameMap,
    fetchData,
  };
};

export default useSubTopicTrend;
